import React, { useEffect, useState } from 'react';
import {
  Layout,
  Button,
  Icon,
  message,
  Alert,
  Switch,
  Modal,
  Tooltip,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../Api/api';
import TitleAndNew from '../../Components/TitleAndNew/TitleAndNew';
import TimeSheetRow from '../../Components/TimeSheetRow/TimeSheetRow';
import TimeSheetSelectUser from '../../Components/TimeSheetSelectUser/TimeSheetSelectUser';
import style from './style.module.css';
import 'moment/locale/en-gb';
import {
  REQUEST_GET_AUTH_USER_DATA,
  REQUEST_GET_HOLIDAY_INFO,
  REQUEST_GET_TIME_SHEET_DATA,
  REQUEST_GET_TIME_SHEET_DATA_OF_USER,
  SELECT_USER_FOR_TIMESHEET,
  SET_SHOW_TEAM_IN_TIMESHEET,
} from '../../Redux/constants/constants';
import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  format,
  parseISO,
  compareAsc,
} from 'date-fns';
import checkValuesInNewTimeLog from '../../Helper/checkValuesInNewTimeLog';
import mergeTimeSheetEntries from '../../Helper/mergeTimeSheetEntries';
import openErrorNotification from '../../Utils/openErrorNotification';

const { Content } = Layout;
const { confirm } = Modal;

const isDateBeforeClosedWeek = (date, userWeekCloseDate) => {
  const userWeekCloseDateObject = parseISO(userWeekCloseDate + 'T00:00.000Z');
  const entryObject = parseISO(date + 'T00:00.000Z');
  const checkedDate = compareAsc(entryObject, userWeekCloseDateObject);
  return checkedDate === 0 || checkedDate === -1;
};

const TimeSheet = () => {
  const authUserData = useSelector((state) => state.authenticatedUserData);
  const hasAdminRole = authUserData && authUserData.role === 'admin';
  // localTimeLog -> unsaved data in timesheet
  const [localTimeLog, setLocalTimeLog] = useState([]);
  // localData -> saved data in timesheet
  const [localData, setLocalData] = useState([]);
  const [validInput, setValidInput] = useState(true);
  const currentWeekStart = format(
    startOfWeek(new Date(), { weekStartsOn: 1 }),
    'yyyy-MM-dd'
  );
  const currentWeekEnd = format(
    endOfWeek(new Date(), { weekStartsOn: 1 }),
    'yyyy-MM-dd'
  );
  const [startDate, setStartDate] = useState(currentWeekStart);
  const [endDate, setEndDate] = useState(currentWeekEnd);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const showTeamInTimeSheet = useSelector((state) => state.showTeamInTimeSheet);
  const data = useSelector((state) => state.timeSheet);
  const selectedUserIdForTimeSheet = useSelector(
    (state) => state.selectedUserIdForTimeSheet
  );
  const [canCloseWeek, setCanCloseWeek] = useState(false);
  const [weekClosed, setWeekClosed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const userWeekCloseDate = (authUserData && authUserData.weekCloseDate) || '';
  useEffect(() => {
    // reset component state
    dispatch({ type: SET_SHOW_TEAM_IN_TIMESHEET, payload: false });
    dispatch({ type: SELECT_USER_FOR_TIMESHEET, payload: '' });
    dispatch({ type: REQUEST_GET_HOLIDAY_INFO });
  }, []);

  useEffect(() => {
    if (selectedUserIdForTimeSheet) {
      dispatch({
        type: REQUEST_GET_TIME_SHEET_DATA_OF_USER,
        payload: { startDate, endDate, userId: selectedUserIdForTimeSheet },
      });
    } else {
      dispatch({
        type: REQUEST_GET_TIME_SHEET_DATA,
        payload: { startDate, endDate },
      });
    }
    if (authUserData) {
      const result = isDateBeforeClosedWeek(
        endDate,
        authUserData.weekCloseDate
      );
      setWeekClosed(result);
    }
    setCanCloseWeek(() => checkCanCloseWeek());
  }, [startDate, endDate, selectedUserIdForTimeSheet, authUserData]);

  // save data to local state
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  useEffect(() => {
    checkValuesInNewTimeLog(localTimeLog, setValidInput);
  }, [localTimeLog]);

  useEffect(() => {
    setLocalTimeLog([]);
  }, [showTeamInTimeSheet, selectedUserIdForTimeSheet]);

  const checkCanCloseWeek = () =>
    endDate !== currentWeekEnd && !showTeamInTimeSheet;

  const minusOneWeek = () => {
    setStartDate(format(addWeeks(new Date(startDate), -1), 'yyyy-MM-dd'));
    setEndDate(format(addWeeks(new Date(endDate), -1), 'yyyy-MM-dd'));
    setLocalTimeLog([]);
  };

  const plusOneWeek = () => {
    setStartDate(format(addWeeks(new Date(startDate), 1), 'yyyy-MM-dd'));
    setEndDate(format(addWeeks(new Date(endDate), 1), 'yyyy-MM-dd'));
    setLocalTimeLog([]);
  };

  const currentWeek = () => {
    setStartDate(currentWeekStart);
    setEndDate(currentWeekEnd);
    setLocalTimeLog([]);
  };

  const onSwitchChange = (checked) => {
    dispatch({ type: SET_SHOW_TEAM_IN_TIMESHEET, payload: checked });
    dispatch({ type: SELECT_USER_FOR_TIMESHEET, payload: '' });
  };

  const showConfirm = () => {
    confirm({
      title: 'Zamknij timesheet',
      icon: <Icon type="lock" theme="twoTone" />,
      content: (
        <div>
          {userWeekCloseDate && (
            <p>
              od <strong>{`${userWeekCloseDate}`}</strong>
            </p>
          )}
          <p>
            do <strong>{`${endDate}`}</strong>
          </p>
        </div>
      ),
      okText: 'Zamknij',
      cancelText: 'Anuluj',
      onOk() {
        return api
          .closeWeek({ weekEndDate: endDate })
          .then((res) => {
            dispatch({
              type: REQUEST_GET_AUTH_USER_DATA,
              payload: authUserData.email,
            });
          })
          .catch();
      },
      onCancel() {},
    });
  };

  const handleInputChange = (e, stageId, date, area) => {
    // mapping of checkbox to 8h
    // ant Input Number does not have e.target
    // this check is for separating checkbox from Input Number

    let value;
    if (
      e &&
      e.target &&
      e.target.type === 'checkbox' &&
      e.target.checked === true
    ) {
      value = 8;
    } else if (
      e &&
      e.target &&
      e.target.type === 'checkbox' &&
      e.target.checked === false
    ) {
      value = '';
    } else {
      value = parseFloat(e) || '';
    }

    // create new entry
    const newEntry = {
      stageId,
      userId: selectedUserIdForTimeSheet
        ? selectedUserIdForTimeSheet
        : authUserData.id, // TODO czy to jest madre wysylac tak userId, moze lepiej brac w node z emaila?? z drugiej strony jak bede chcial wprowadzic funkcjonalnosc taka ze admin moze modyfikowac wpis usera to wlasnie tu musial bym podmienic to Id... hmmmmmmm
      timeSpent: isNaN(value) ? '' : value,
      date,
      local: true,
      area,
    };

    // delete entry from local time log if it exists
    // example: when user made a mistake and
    // wants to delete local 8 h
    for (let i = 0; i < localTimeLog.length; i++) {
      const element = localTimeLog[i];
      // find entry to delete
      if (
        newEntry.timeSpent === '' &&
        element.stageId === newEntry.stageId &&
        element.userId === newEntry.userId &&
        element.date === newEntry.date &&
        element.area === newEntry.area
      ) {
        // delete the entry
        localTimeLog.splice(i, 1);
        setLocalTimeLog([...localTimeLog]);
        // return;
      }
    }

    // update local time log with entry
    for (let i = 0; i < localTimeLog.length; i++) {
      const element = localTimeLog[i];
      if (
        element.stageId === newEntry.stageId &&
        element.userId === newEntry.userId &&
        element.date === newEntry.date &&
        element.area === newEntry.area
      ) {
        element.timeSpent = newEntry.timeSpent;
        setLocalTimeLog([...localTimeLog]);
        return;
      }
    }
    setLocalTimeLog([...localTimeLog, newEntry]);
    let newTimeLog = [...localTimeLog, newEntry];
    setLocalData(mergeTimeSheetEntries(localData, newTimeLog));
  };

  /**
   * Split into 2 api calls:
   * first call to save all hours
   * second call based on entries from first to determine if some entries need overtime calculation
   */
  const handleSave = () => {
    if (localTimeLog.length === 0) return;
    setLoading(true);
    api
      .saveTimeLog(localTimeLog)
      .then((entries) => api.saveOvertime(entries))
      .then(() => {
        setLoading(false);
        setLocalTimeLog([]);
        message.success('Zapisano pomyślnie', 2);
        if (showTeamInTimeSheet && selectedUserIdForTimeSheet) {
          dispatch({
            type: REQUEST_GET_TIME_SHEET_DATA_OF_USER,
            payload: { startDate, endDate, userId: selectedUserIdForTimeSheet },
          });
        } else {
          dispatch({
            type: REQUEST_GET_TIME_SHEET_DATA,
            payload: { startDate, endDate },
          });
        }
      })
      .catch((e) => {
        setLoading(false);
        // message.error('Błąd zapisu', 2);
        // message.error('Skontaktuj sie z supportem', 3);
        openErrorNotification(e);
      });
  };

  return (
    <Layout>
      <Content style={{ margin: '36px', maxWidth: '1100px' }}>
        <TitleAndNew titleText={'Timesheet'} />
        <div
          style={{
            background: '#fff',
            padding: '16px',
            borderRadius: '8px',
          }}
        >
          <div className={style.buttonsWrapper}>
            <div>
              <Button className={style.withSmallMargin} onClick={minusOneWeek}>
                <Icon type="left" />
              </Button>
              <span
                className={style.currentDate}
              >{`${startDate} | ${endDate}`}</span>
              <Button className={style.withSmallMargin} onClick={plusOneWeek}>
                <Icon type="right" />
              </Button>
              <Button className={style.withSmallMargin} onClick={currentWeek}>
                Aktualny tydzień
              </Button>
            </div>
            <div>
              {weekClosed ? (
                <Tooltip
                  className={style.withSmallMargin}
                  title="tydzień zamknięty"
                >
                  <Icon type="lock" theme="twoTone" />
                </Tooltip>
              ) : null}

              {!hasAdminRole ? (
                canCloseWeek ? (
                  <Button
                    className={style.withSmallMargin}
                    onClick={showConfirm}
                    disabled={weekClosed}
                  >
                    Zamknij tydzień
                  </Button>
                ) : (
                  <Tooltip
                    className={style.withSmallMargin}
                    title={
                      showTeamInTimeSheet
                        ? 'nie można zamykać w imieniu pracownika'
                        : 'nie można zamknąć aktualnego tygodnia'
                    }
                  >
                    <Button disabled={true}>Zamknij tydzień</Button>
                  </Tooltip>
                )
              ) : null}

              <Button
                className={style.withSmallMargin}
                onClick={handleSave}
                type={localTimeLog.length > 0 ? 'primary' : 'default'}
                loading={loading}
                disabled={!validInput}
              >
                Zapisz
              </Button>

              {hasAdminRole && (
                <>
                  <Switch
                    checkedChildren={
                      <>
                        <Icon type="check" />
                        <span> Zespół</span>
                      </>
                    }
                    unCheckedChildren={
                      <>
                        <span>Zespół </span>
                        <Icon type="close" />
                      </>
                    }
                    // unCheckedChildren={<Icon type="close" />}
                    // unCheckedChildren={<Icon type="close" />}
                    onChange={onSwitchChange}
                  />
                  {/*<span style={{ marginLeft: '12px' }}>Zespół</span>*/}
                </>
              )}
            </div>
            {/*<Button className={style.withSmallMargin} onClick={minusOneWeek}>*/}
            {/*  <Icon type="left" />*/}
            {/*</Button>*/}
            {/*<span*/}
            {/*  className={style.currentDate}*/}
            {/*>{`${startDate} | ${endDate}`}</span>*/}
            {/*<Button className={style.withBigMargin} onClick={plusOneWeek}>*/}
            {/*  <Icon type="right" />*/}
            {/*</Button>*/}

            {/*<Button className={style.withSmallMargin} onClick={currentWeek}>*/}
            {/*  Aktualny tydzień*/}
            {/*</Button>*/}
            {/*<Button*/}
            {/*  className={style.buttonMargin}*/}
            {/*  onClick={handleSave}*/}
            {/*  type={localTimeLog.length > 0 ? 'primary' : 'default'}*/}
            {/*  loading={loading}*/}
            {/*  disabled={!validInput}*/}
            {/*>*/}
            {/*  Zapisz*/}
            {/*</Button>*/}
            {/*{authUserData && authUserData.role === 'admin' && (*/}
            {/*  <>*/}
            {/*    <Switch*/}
            {/*      style={{ marginLeft: '60px' }}*/}
            {/*      checkedChildren={<Icon type="check" />}*/}
            {/*      unCheckedChildren={<Icon type="close" />}*/}
            {/*      onChange={onSwitchChange}*/}
            {/*    />*/}
            {/*    <span style={{ marginLeft: '12px' }}>Zespół</span>*/}
            {/*  </>*/}
            {/*)}*/}
          </div>
          {showTeamInTimeSheet && (
            <div className={style.buttonsWrapper}>
              <div>
                <TimeSheetSelectUser />
              </div>
            </div>
          )}
          {!validInput && (
            <Alert
              className={style.withSmallMargin}
              showIcon
              message="Niepoprawna wartość. Proszę wprowadzić wielokrotność 0.5h"
              type="error"
            />
          )}
          {!showTeamInTimeSheet &&
            localData.length > 0 &&
            localData.map((row) => (
              <TimeSheetRow
                key={row.key}
                data={row}
                handleInputChange={handleInputChange}
              />
            ))}
          {showTeamInTimeSheet &&
            selectedUserIdForTimeSheet &&
            localData.length > 0 &&
            localData.map((row) => (
              <TimeSheetRow
                key={row.key}
                data={row}
                handleInputChange={handleInputChange}
              />
            ))}
        </div>
      </Content>
    </Layout>
  );
};

export default TimeSheet;
